import React, { Component } from 'react';
import { Flex, HStack, Link } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import BreakView from './BreakView';
import PageView from './PageView';

class Pagination extends Component {
  handlePreviousPage = () => {
    const { currentPage, handlePageSelection } = this.props;

    if (currentPage > 0) {
      handlePageSelection(currentPage - 1);
    }
  };

  handleNextPage = () => {
    const { pageCount, currentPage, handlePageSelection } = this.props;

    if (currentPage < pageCount) {
      handlePageSelection(currentPage + 1);
    }
  };

  getEventListener = handlerFunction => {
    const { eventListener } = this.props;
    return { [eventListener]: handlerFunction };
  }

  getForwardJump() {
    const { currentPage, pageCount, pageRangeDisplayed } = this.props;

    const forwardJump = currentPage + pageRangeDisplayed;
    return forwardJump >= pageCount ? pageCount - 1 : forwardJump;
  }

  getBackwardJump() {
    const { pageRangeDisplayed, currentPage } = this.props;

    const backwardJump = currentPage - pageRangeDisplayed;
    return backwardJump < 0 ? 0 : backwardJump;
  }

  handleBreakClick = index => {
    const { currentPage, handlePageSelection } = this.props;
    handlePageSelection(currentPage < index ? this.getForwardJump() : this.getBackwardJump());
  };

  ariaLabelBuilder(pageIndex) {
    const isCurrentPage = pageIndex === this.props.currentPage;
    if (
      this.props.ariaLabelBuilder
      && pageIndex >= 0
      && pageIndex < this.props.pageCount
    ) {
      let label = this.props.ariaLabelBuilder(pageIndex + 1, isCurrentPage);
      // DEPRECATED: The extraAriaContext prop was used to add additional context
      // to the aria-label. Users should now use the ariaLabelBuilder instead.
      if (this.props.extraAriaContext && !isCurrentPage) {
        label = `${label} ${this.props.extraAriaContext}`;
      }
      return label;
    }

    return '';
  }

  getPageElement(index) {
    const {
      currentPage,
      extraAriaContext,
      handlePageSelection
    } = this.props;

    const page = index + 1;

    return (
      <PageView
        ariaLabel={`page ${page}`}
        extraAriaContext={extraAriaContext}
        getEventListener={this.getEventListener}
        isCurrentPage={currentPage === page}
        key={page - 1}
        page={page}
        pageSelectedHandler={() => handlePageSelection(page)}
      />
    );
  }

  pagination = () => {
    const items = [];
    const {
      pageRangeDisplayed,
      pageCount,
      marginPagesDisplayed,
      breakLabel,
      currentPage
    } = this.props;

    if (pageCount <= pageRangeDisplayed) {
      for (let index = 0; index < pageCount; index++) {
        items.push(this.getPageElement(index));
      }
    } else {
      let leftSide = pageRangeDisplayed / 2;
      let rightSide = pageRangeDisplayed - leftSide;

      // If the currentPage page index is on the default right side of the pagination,
      // we consider that the new right side is made up of it (= only one break element).
      // If the currentPage page index is on the default left side of the pagination,
      // we consider that the new left side is made up of it (= only one break element).
      if (currentPage > pageCount - pageRangeDisplayed / 2) {
        rightSide = pageCount - currentPage;
        leftSide = pageRangeDisplayed - rightSide;
      } else if (currentPage < pageRangeDisplayed / 2) {
        leftSide = currentPage;
        rightSide = pageRangeDisplayed - leftSide;
      }

      let index;
      let page;
      let breakView;

      const createPageView = i => this.getPageElement(i);

      for (index = 0; index < pageCount; index++) {
        page = index + 1;

        // If the page index is lower than the margin defined,
        // the page has to be displayed on the left side of
        // the pagination.
        if (page <= marginPagesDisplayed) {
          items.push(createPageView(index));
          continue;
        }

        // If the page index is greater than the page count
        // minus the margin defined, the page has to be
        // displayed on the right side of the pagination.
        if (page > pageCount - marginPagesDisplayed) {
          items.push(createPageView(index));
          continue;
        }

        // If the page index is near the currentPage page index
        // and inside the defined range (pageRangeDisplayed)
        // we have to display it (it will create the center
        // part of the pagination).
        if (index >= currentPage - leftSide && index <= currentPage + rightSide) {
          items.push(createPageView(index));
          continue;
        }

        // If the page index doesn't meet any of the conditions above,
        // we check if the last item of the current "items" array
        // is a break element. If not, we add a break element, else,
        // we do nothing (because we don't want to display the page).
        if (breakLabel && items[items.length - 1] !== breakView) {
          breakView = (
            <BreakView
              breakHandler={() => this.handleBreakClick(index)}
              breakLabel={breakLabel}
              getEventListener={this.getEventListener}
              key={index}
            />
          );
          items.push(breakView);
        }
      }
    }
    return items;
  };

  render() {
    const {
      pageCount,
      previousLabel,
      previousAriaLabel,
      prevRel,
      nextLabel,
      nextAriaLabel,
      nextRel,
      currentPage,
      style = {}
    } = this.props;

    const previousAriaDisabled = currentPage === 0 ? 'true' : 'false';
    const nextAriaDisabled = currentPage === pageCount - 1 ? 'true' : 'false';

    return (
      <Flex
        alignItems="center"
        as="ul"
        justifyContent="space-around"
        m="0 auto"
        maxWidth="400px"
        py={4}
        style={{ listStyle: 'none', ...style }}
      >
        <PageView
          ariaLabel={previousAriaLabel}
          disabled={currentPage === 1}
          getEventListener={this.getEventListener}
          page={previousLabel}
          pageSelectedHandler={() => this.handlePreviousPage()}
        />

        {this.pagination()}

        <PageView
          ariaLabel={nextAriaLabel}
          disabled={currentPage === pageCount}
          getEventListener={this.getEventListener}
          page={nextLabel}
          pageSelectedHandler={() => this.handleNextPage()}
        />

      </Flex>
    );
  }
}

Pagination.propTypes = {
  pageCount: PropTypes.number.isRequired,
  pageRangeDisplayed: PropTypes.number,
  marginPagesDisplayed: PropTypes.number,
  previousLabel: PropTypes.node,
  previousAriaLabel: PropTypes.string,
  prevRel: PropTypes.string,
  nextLabel: PropTypes.node,
  nextAriaLabel: PropTypes.string,
  nextRel: PropTypes.string,
  breakLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  currentPage: PropTypes.number.isRequired,
  disableInitialCallback: PropTypes.bool,
  eventListener: PropTypes.string,
  handlePageSelection: PropTypes.func.isRequired,
  style: PropTypes.object
};

Pagination.defaultProps = {
  pageRangeDisplayed: 2,
  marginPagesDisplayed: 3,
  previousLabel: 'Previous',
  previousAriaLabel: 'Previous page',
  prevRel: 'prev',
  nextLabel: 'Next',
  nextAriaLabel: 'Next page',
  nextRel: 'next',
  breakLabel: '...',
  disableInitialCallback: false,
  eventListener: 'onClick'
};

export default Pagination;
